import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, FileText, Download, Eye } from 'lucide-react';
import {
  useClientReports,
  useClientRetractions,
  type ClientRetraction,
} from '../../lib/api/queries';

/**
 * Client landing page — a list of the reports DPAS has published to the
 * caller's organisation. If any reports have been retracted in the last
 * 30 days, a dismissable banner surfaces at the top explaining that
 * the copies the client may have already downloaded are still theirs but
 * cannot be re-fetched from here.
 */
export function ClientReportsListPage() {
  const { data: reports, isLoading, error } = useClientReports();
  return (
    <div className="space-y-6">
      <RetractionBanner />

      <header>
        <h1 className="text-2xl font-semibold text-gray-900">Your reports</h1>
        <p className="text-sm text-gray-500 mt-1">
          Every audit and DPIA report DPAS has published to your organisation.
        </p>
      </header>

      {isLoading && (
        <p className="text-sm text-gray-500">Loading…</p>
      )}
      {error && (
        <p className="text-sm text-danger-500">
          We couldn't load your reports. Try refreshing; if this persists,
          contact your DPAS consultant.
        </p>
      )}
      {reports && reports.length === 0 && !isLoading && (
        <div className="rounded-md border border-gray-200 bg-white p-8 text-center">
          <FileText className="w-8 h-8 mx-auto text-gray-400 mb-2" aria-hidden />
          <p className="text-sm font-medium text-gray-700">No reports yet.</p>
          <p className="text-xs text-gray-500 mt-1">
            When DPAS publishes a report to your organisation it will appear
            here. Watch this space.
          </p>
        </div>
      )}

      {reports && reports.length > 0 && (
        <ul className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
          {reports.map(r => (
            <li key={r.snapshotId} className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-md bg-accent-alt-50 text-accent-alt-500
                              flex items-center justify-center shrink-0">
                <FileText className="w-5 h-5" aria-hidden />
              </div>

              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">
                  {r.auditTitle}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {r.label ? <><span className="font-medium">{r.label}</span> · </> : null}
                  Published {formatDate(r.publishedAtUtc)}
                  {r.publishedByDisplayName ? ` by ${r.publishedByDisplayName}` : null}
                </p>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <Link
                  to={`/client/reports/${r.snapshotId}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium
                             text-white bg-accent-alt-600 hover:bg-accent-alt-700 rounded-md
                             transition-colors"
                >
                  <Eye className="w-4 h-4" aria-hidden /> View
                </Link>
                <a
                  href={`/api/client/reports/${r.snapshotId}/pdf`}
                  download
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium
                             text-gray-700 bg-white border border-gray-300 hover:bg-gray-50
                             rounded-md transition-colors"
                >
                  <Download className="w-4 h-4" aria-hidden /> PDF
                </a>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------

const RETRACTION_DISMISS_KEY = 'dpas.client.retractions.dismissedIds';

/**
 * Banner surfaced when DPAS staff have retracted a report in the last
 * 30 days. Dismissable per-report via localStorage (v1) — a server-side
 * "acknowledged" state can come in a follow-up if we find users want it.
 */
function RetractionBanner() {
  const { data: retractions } = useClientRetractions();
  const [dismissedIds, setDismissedIds] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(RETRACTION_DISMISS_KEY);
      return raw ? (JSON.parse(raw) as string[]) : [];
    } catch { return []; }
  });

  useEffect(() => {
    try {
      localStorage.setItem(RETRACTION_DISMISS_KEY, JSON.stringify(dismissedIds));
    } catch { /* localStorage disabled — best-effort only */ }
  }, [dismissedIds]);

  const undismissed = (retractions ?? [])
    .filter(r => !dismissedIds.includes(r.snapshotId));
  if (undismissed.length === 0) return null;

  return (
    <div className="rounded-md border border-warning-200 bg-warning-50 p-4">
      <div className="flex gap-3">
        <AlertTriangle className="w-5 h-5 text-warning-500 shrink-0 mt-0.5" aria-hidden />
        <div className="flex-1">
          <p className="text-sm font-medium text-warning-900">
            {undismissed.length === 1
              ? 'A report has been retracted by DPAS staff'
              : `${undismissed.length} reports have been retracted by DPAS staff`}
          </p>
          <p className="text-xs text-warning-800 mt-1">
            Any copies you already downloaded remain yours; you will no
            longer be able to view or re-download them here.
          </p>
          <ul className="mt-3 space-y-1.5">
            {undismissed.map(r => (
              <RetractionRow
                key={r.snapshotId}
                retraction={r}
                onDismiss={() => setDismissedIds(ids => [...ids, r.snapshotId])}
              />
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function RetractionRow({
  retraction,
  onDismiss,
}: {
  retraction: ClientRetraction;
  onDismiss: () => void;
}) {
  const { auditTitle, label, unpublishedAtUtc, unpublishedByDisplayName } = retraction;
  return (
    <li className="flex items-start gap-2 text-xs text-warning-900">
      <span className="flex-1">
        <span className="font-medium">{auditTitle}</span>
        {label ? <> — {label}</> : null}
        {' · retracted '}{formatDate(unpublishedAtUtc)}
        {unpublishedByDisplayName ? ` by ${unpublishedByDisplayName}` : null}
      </span>
      <button
        type="button"
        onClick={onDismiss}
        className="text-warning-700 hover:text-warning-900 underline"
      >
        Dismiss
      </button>
    </li>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}
